import { useMemo, useState } from "react";

import { GroupMappingsSection } from "./sections/GroupMappingsSection";
import { BreakGlassOwnersSection } from "./sections/BreakGlassOwnersSection";
import { WorkOsOrganizationSection } from "./sections/WorkOsOrganizationSection";
import { ConnectionSection } from "./sections/ConnectionSection";
import { DefaultWorkspaceSection } from "./sections/DefaultWorkspaceSection";
import { SignInPolicySection } from "./sections/SignInPolicySection";
import { AuditLogSection } from "./sections/AuditLogSection";

import LoadingSpinner from "@/components/LoadingSpinner";
import { useActiveOrganization } from "@/hooks/organizations";
import { useIdentitySettings } from "@/queries/identity/useIdentitySettings";
import { useCurrentUser } from "@/queries/users";

/**
 * Enterprise Identity admin tab. Rendered only for Owners on orgs with
 * `is_enterprise_identity_enabled`; the org details tab strip hides it
 * otherwise.
 */
interface EnterpriseIdentityPageProps {
  /** Optional override; defaults to the active organization. */
  organizationId?: number;
}

const EnterpriseIdentityPage = ({
  organizationId: organizationIdProp,
}: EnterpriseIdentityPageProps = {}) => {
  const { data: activeOrganization } = useActiveOrganization();
  const { data: currentUser } = useCurrentUser();
  const organizationId = organizationIdProp ?? activeOrganization?.id ?? null;

  const isOwner = useMemo(() => {
    if (!currentUser || organizationId === null) {
      return false;
    }
    return currentUser.memberships.organizations.some(
      m => m.organization_id === organizationId && m.organization_role?.name === "owner",
    );
  }, [currentUser, organizationId]);

  const { data, isLoading } = useIdentitySettings(organizationId);
  const [sectionError, setSectionError] = useState<string | null>(null);

  if (organizationId === null || !currentUser) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white p-8 text-center">
        <h2 className="mb-2 text-lg font-semibold text-gray-900">Owner access required</h2>
        <p className="text-sm text-gray-600">
          Only organization Owners can configure enterprise identity settings. Ask an Owner of{" "}
          <span className="font-semibold">{activeOrganization?.name}</span> to manage SSO and
          Directory Sync.
        </p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data.feature_enabled) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        Enterprise identity is not enabled for this organization.
      </div>
    );
  }

  // Defensive narrowing — `feature_enabled` implies `settings` server-side.
  if (!data.settings) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        Enterprise identity is enabled but settings are not yet provisioned. Try refreshing — if the
        issue persists, contact PromptLayer support.
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 p-2 pb-8">
      {sectionError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {sectionError}
        </div>
      )}

      <WorkOsOrganizationSection
        organizationId={organizationId}
        settings={data.settings}
        onError={setSectionError}
      />
      <ConnectionSection
        organizationId={organizationId}
        settings={data.settings}
        onError={setSectionError}
      />
      <SignInPolicySection
        organizationId={organizationId}
        settings={data.settings}
        onError={setSectionError}
      />
      <BreakGlassOwnersSection organizationId={organizationId} onError={setSectionError} />
      <DefaultWorkspaceSection
        organizationId={organizationId}
        settings={data.settings}
        onError={setSectionError}
      />
      <GroupMappingsSection organizationId={organizationId} onError={setSectionError} />
      <AuditLogSection organizationId={organizationId} />
    </div>
  );
};

export default EnterpriseIdentityPage;
